import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useLocalParticipant, useConnectionState, useParticipants } from "@livekit/components-react";
import { useAppDispatch } from "../../../store/hooks";
import { RootState } from "../../../store";
import {
  toggleMemberMute,
  leaveTeamSession,
  endTeamSession,
} from "../../../store/slices/teamFocusSessionSlice";
import {
  updateTeamTask,
  deleteTeamTask,
} from "../../../store/slices/teamFocusSession/taskThunks";
import {
  deleteTeamNote,
} from "../../../store/slices/teamFocusSession/noteThunks";
import { Note, TeamCheckin } from "../../../store/slices/teamFocusSession/types";
import { TaskModal, NoteModal, CheckInModal } from "../Dashboard";
import ConfirmModal from "../../ConfirmModal";
import SessionHeader from "./SessionHeader";
import VideoGrid from "./VideoGrid";
import RightSidebar from "./RightSidebar";
import InvalidSession from "./InvalidSession";
import SessionEndedModal from "./SessionEndedModal";
import { toast } from "react-toastify";

interface SessionContentProps {
  teamId: string;
  sess: any;
  isSessionInvalid: boolean;
  handlers: any;
  cameraEnabled: boolean;
  microphoneEnabled: boolean;
}

const SessionContent: React.FC<SessionContentProps> = ({ teamId, sess, isSessionInvalid, handlers, cameraEnabled, microphoneEnabled }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentUserId = useSelector((state: RootState) => state.user?.user?.id ?? '');

  const { localParticipant, isMicrophoneEnabled, isCameraEnabled, isScreenShareEnabled } = useLocalParticipant();
  const connectionState = useConnectionState();
  const participants = useParticipants();

  // Check if current user is host
  const isHost = useSelector((state: RootState) => {
    if (!teamId) return false;
    const session = state.teamFocusSessions.sessions[teamId];
    return session?.isHost || session?.hostId === currentUserId;
  });

  // Check if session was ended by host
  const sessionEndedByHost = useSelector((state: RootState) => {
    if (!teamId) return false;
    return state.teamFocusSessions.sessions[teamId]?.sessionEndedByHost || false;
  });

  // Check if any other participant is sharing screen
  const othersAreSharing = useMemo(() => {
    return participants.some(p => !p.isLocal && p.isScreenShareEnabled);
  }, [participants]);

  // UI state
  const [activeModal, setActiveModal] = useState<"task" | "note" | "checkin" | null>(null);
  const [editingTask, setEditingTask] = useState<any>(null);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [editingCheckin, setEditingCheckin] = useState<TeamCheckin | null>(null);
  const [isEndSessionModalOpen, setIsEndSessionModalOpen] = useState(false);
  const [showSessionEndedModal, setShowSessionEndedModal] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const isMuted = useSelector((state: RootState) => {
    if (!teamId) return true;
    const members = state.teamFocusSessions.sessions[teamId]?.teamMembers;
    return members?.find((m: any) => m.id === currentUserId)?.isMuted ?? true;
  });

  // Compute actual states from LiveKit
  const actualIsMuted = localParticipant ? !isMicrophoneEnabled : isMuted;
  const actualIsVideoOn = localParticipant ? isCameraEnabled : false;
  const actualIsScreenSharing = localParticipant ? isScreenShareEnabled : false;

  const handleEndSession = useCallback(() => {
    setIsEndSessionModalOpen(true);
  }, []);

  // Show modal when session is ended by host
  useEffect(() => {
    if (sessionEndedByHost && !isHost) {
      setShowSessionEndedModal(true);
    }
  }, [sessionEndedByHost, isHost]);

  const handleSessionEndedByHostOkay = useCallback(() => {
    setShowSessionEndedModal(false);
    navigate("/dashboard");
  }, [navigate]);

  // Elapsed time (hh:mm) - updates every minute
  const [elapsedTime, setElapsedTime] = useState('00:00');
  useEffect(() => {
    const sessionStartTime = sess?.sessionStartedAt
      ? new Date(sess.sessionStartedAt).getTime()
      : sess?.createdAt
        ? new Date(sess.createdAt).getTime()
        : Date.now();

    const updateElapsed = () => {
      const now = Date.now();
      const elapsedMs = Math.max(0, now - sessionStartTime);
      const totalMinutes = Math.floor(elapsedMs / 60000);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      setElapsedTime(
        `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
      );
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 60000);
    return () => clearInterval(interval);
  }, [sess?.sessionStartedAt, sess?.createdAt]);

  const handleConfirmEndSession = useCallback(() => {
    if (!teamId) return;

    if (isHost) {
      // Host ends the session for everyone
      dispatch(endTeamSession(teamId));
    } else {
      // Non-host participant just leaves
      dispatch(leaveTeamSession(teamId));
    }

    setIsEndSessionModalOpen(false);
    navigate("/dashboard");
  }, [dispatch, teamId, isHost, navigate]);

  // Handler for host to end session (called from VideoGrid)
  const handleHostEndSession = useCallback(() => {
    if (!teamId) return;
    // This will trigger session.ended event for all participants
    dispatch(endTeamSession(teamId));
    navigate("/dashboard");
  }, [dispatch, teamId]);

  // Handler for non-host to leave (called from VideoGrid)
  const handleNonHostLeave = useCallback(() => {
    if (!teamId) return;
    dispatch(leaveTeamSession(teamId));
    navigate("/dashboard");
  }, [dispatch, teamId, navigate]);

  const handleToggleMute = useCallback(() => {
    if (localParticipant) {
      localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled);
    }
    dispatch(
      toggleMemberMute({
        teamId: teamId!,
        memberId: currentUserId,
        isMuted: !actualIsMuted,
      })
    );
  }, [localParticipant, isMicrophoneEnabled, dispatch, teamId, currentUserId, actualIsMuted]);

  const handleToggleSidebar = useCallback(() => {
    setIsSidebarOpen(prev => !prev);
  }, []);

  const handleToggleVideo = useCallback(async () => {
    if (connectionState !== 'connected') {
      toast.error('Please wait for the room to connect');
      return;
    }
    if (isScreenShareEnabled) {
      toast.error('Please stop screen sharing before turning on your video');
      return;
    }
    if (localParticipant) {
      try {
        await localParticipant.setCameraEnabled(!isCameraEnabled);
      } catch (error) {
        console.error("Could not toggle camera. Check permissions.", error);
        toast.error("Could not toggle camera. Please check your permissions.");
      }
    }
  }, [localParticipant, isCameraEnabled, isScreenShareEnabled, connectionState]);

  const handleToggleScreenShare = useCallback(async () => {
    if (connectionState !== 'connected') {
      toast.error('Please wait for the room to connect');
      return;
    }
    if (localParticipant) {
      try {
        // If user is about to start sharing (not currently sharing)
        if (!isScreenShareEnabled) {
          // Check if someone else is already sharing
          if (othersAreSharing) {
            toast.error('Screen sharing is already in progress. Please stop the current share session before starting your own.');
            return;
          }
          // Turn off video when starting to share
          if (isCameraEnabled) {
            await localParticipant.setCameraEnabled(false);
          }
        }
        await localParticipant.setScreenShareEnabled(!isScreenShareEnabled);
      } catch (error) {
        console.error("Could not toggle screen share. Check permissions.", error);
        toast.error("Could not toggle screen share. Please check your permissions.");
      }
    }
  }, [localParticipant, isScreenShareEnabled, isCameraEnabled, othersAreSharing, connectionState]);

  // Task handlers
  const handleToggleTask = useCallback((taskId: string) => {
    if (!teamId) return;
    const task = sess.tasks?.find((t: any) => t.id === taskId);
    if (!task) return;
    const newStatus = task.status === "done" ? "todo" : "done";
    dispatch(updateTeamTask({ teamId, taskId, updates: { status: newStatus } }));
  }, [teamId, sess.tasks, dispatch]);

  const handleDeleteTask = useCallback((taskId: string) => {
    if (!teamId) return;
    dispatch(deleteTeamTask({ teamId, taskId }));
  }, [teamId, dispatch]);

  const handleEditTask = useCallback((task: any) => {
    setEditingTask(task);
    setActiveModal("task");
  }, []);

  const handleAddTask = useCallback(() => {
    setEditingTask(null);
    setActiveModal("task");
  }, []);

  // Note handlers
  const handleDeleteNote = useCallback((noteId: string) => {
    if (!teamId) return;
    dispatch(deleteTeamNote({ teamId, noteId }));
  }, [teamId, dispatch]);

  const handleEditNote = useCallback((note: Note) => {
    setEditingNote(note);
    setActiveModal("note");
  }, []);

  const handleAddNote = useCallback(() => {
    setEditingNote(null);
    setActiveModal("note");
  }, []);

  return (
    <>
      <SessionHeader
        teamId={teamId}
        goal={sess.goal || ""}
        elapsedMinutes={elapsedTime}
      />

      {isSessionInvalid ? (
        <InvalidSession />
      ) : (
        <div className="flex-1 flex overflow-hidden">
          {/* Main Video Area */}
          <div className="flex-1 overflow-hidden p-3 sm:p-4">
            <VideoGrid
              teamId={teamId}
              isHost={isHost}
              isMuted={actualIsMuted}
              isVideoOn={actualIsVideoOn}
              isScreenSharing={actualIsScreenSharing}
              isVideoDisabled={isScreenShareEnabled}
              onToggleMute={handleToggleMute}
              onToggleVideo={handleToggleVideo}
              onToggleScreenShare={handleToggleScreenShare}
              onEndCall={handleNonHostLeave}
              onEndSession={handleHostEndSession}
              onToggleSidebar={handleToggleSidebar}
            />
          </div>

          {/* Right Sidebar - Always Visible on Desktop, Slide-in on Mobile */}
          <RightSidebar
            tasks={sess.tasks || []}
            notes={sess.notes || []}
            tasksLoading={sess.tasksLoading || false}
            notesLoading={sess.notesLoading || false}
            teamId={teamId}
            session={sess}
            currentUserId={currentUserId}
            onToggleTask={handleToggleTask}
            onDeleteTask={handleDeleteTask}
            onEditTask={handleEditTask}
            onDeleteNote={handleDeleteNote}
            onEditNote={handleEditNote}
            onAddTask={handleAddTask}
            onAddNote={handleAddNote}
            isOpen={isSidebarOpen}
            onClose={() => setIsSidebarOpen(false)}
          />
        </div>
      )}

      {/* Modals */}
      <TaskModal
        isOpen={activeModal === "task"}
        onClose={() => {
          setActiveModal(null);
          setEditingTask(null);
        }}
        initialTask={editingTask}
      />
      <NoteModal
        isOpen={activeModal === "note"}
        onClose={() => {
          setActiveModal(null);
          setEditingNote(null);
        }}
        initialNote={editingNote}
      />
      <CheckInModal
        isOpen={activeModal === "checkin"}
        onClose={() => {
          setActiveModal(null);
          setEditingCheckin(null);
        }}
        onAdd={handlers.handleCheckInAdd}
        onUpdate={handlers.handleCheckinUpdate}
        initialCheckin={editingCheckin}
      />

      <ConfirmModal
        open={isEndSessionModalOpen}
        onClose={() => setIsEndSessionModalOpen(false)}
        onConfirm={handleConfirmEndSession}
        title="End Session"
        message="Are you sure you want to end this session early?"
        confirmText="End Session"
        cancelText="Cancel"
        type="danger"
      />

      <SessionEndedModal
        open={showSessionEndedModal}
        onOkay={handleSessionEndedByHostOkay}
      />
    </>
  );
};

export default SessionContent;
